import React, { useEffect, useState } from "react";
import API from "../../services/api";
import {
    OPC_DATA_TYPES,
    ON_CHANGE_INTERVAL_PRESETS,
    ON_CHANGE_INTERVAL_SECONDS,
    RECORDING_MODES,
    intervalLabel,
    browserDraft,
    creationPayload,
    dataTypeForSignal,
    decimalDefault,
    groupChoices,
    manualDefaults,
    nodeShareState,
    validateTagDraft,
} from "./tagCreation";

const fieldError = (errors, key) => errors[key] || "";

export default function TagCreationForm({
    idPrefix,
    mode,
    servers,
    groups,
    listing,
    details,
    serverId,
    alreadyRegistered,
    submitLabel,
    onCreated,
}) {
    const manual = mode !== "browser";
    const draft = manual ? null : browserDraft(details, listing, serverId);
    const [values, setValues] = useState(() => (manual ? manualDefaults() : draft.values));
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState("");
    const [banner, setBanner] = useState("");
    const [saving, setSaving] = useState(false);
    const [fetchingName, setFetchingName] = useState(false);
    const [occupants, setOccupants] = useState([]);
    const seed = manual
        ? "manual"
        : [
            listing?.node_id || "",
            draft.nodeDataType,
            draft.values.signalType,
            draft.values.description,
            draft.unsupported ? "unsupported" : "supported",
        ].join("|");

    useEffect(() => {
        if (manual) {
            return;
        }
        setValues(draft.values);
        setErrors({});
        setMessage("");
        setBanner("");
    }, [seed]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!values.server || !String(values.nodeId || "").trim()) {
            setOccupants([]);
            return undefined;
        }
        let active = true;
        API.get("/tags/node-occupants/", {
            params: { server: values.server, node_id: values.nodeId },
        }).then((response) => {
            if (active) {
                setOccupants(Array.isArray(response.data?.occupants) ? response.data.occupants : []);
            }
        }).catch(() => {
            if (active) setOccupants([]);
        });
        return () => {
            active = false;
        };
    }, [values.server, values.nodeId]);

    const share = nodeShareState(occupants, values.isCalculated);

    const update = (key, value) => {
        setValues((current) => {
            const next = { ...current, [key]: value };
            if (key === "signalType") {
                next.opcDataType = dataTypeForSignal(value, current.opcDataType);
                next.decimalPlaces = decimalDefault(value, next.opcDataType);
                if (value === "STRING") {
                    next.isCalculated = false;
                    next.formula = "";
                }
            }
            if (key === "opcDataType" && value === "String") {
                next.signalType = "STRING";
                next.decimalPlaces = "";
                next.isCalculated = false;
                next.formula = "";
            }
            if (key === "isCalculated" && value) {
                next.accessMode = "READ_ONLY";
                if (current.signalType === "STRING") {
                    next.signalType = "ANALOG";
                    next.opcDataType = dataTypeForSignal("ANALOG", current.opcDataType);
                    next.decimalPlaces = decimalDefault("ANALOG", next.opcDataType);
                }
            }
            return next;
        });
        setErrors((current) => ({ ...current, [key]: "" }));
    };

    const choices = groupChoices(groups);
    const sessionCanWrite = draft?.sessionCanWrite === true;
    const unsupported = draft?.unsupported === true;
    const validationOptions = {
        mode: manual ? "manual" : "browser",
        groups,
        sessionCanWrite,
        nodeDataType: draft?.nodeDataType || "",
        unsupported,
        reason: draft?.reason || "",
        nodeShare: share,
    };

    const submit = async (event) => {
        event.preventDefault();
        if (saving) {
            return;
        }
        const nextErrors = validateTagDraft(values, validationOptions);
        setErrors(nextErrors);
        setMessage("");
        setBanner("");
        if (Object.keys(nextErrors).length > 0) {
            setBanner("Complete the required fields before saving.");
            return;
        }
        setSaving(true);
        try {
            const opcNode = manual || !details ? null : {
                node_class: details.identity?.node_class?.value || null,
                data_type_name: details.data_type?.data_type_name?.value || null,
                value_rank: details.data_type?.value_rank?.available === true ? details.data_type.value_rank.value : null,
                session_current_write: details.access?.session_current_write?.value === true,
            };
            const response = await API.post("/tags/", creationPayload(values, opcNode));
            setMessage(values.lifecycle === "archived"
                ? "Archived tag saved. It will not be sampled until it is restored."
                : "Tag added successfully.");
            if (manual) {
                setValues(manualDefaults());
            }
            onCreated?.(response.data);
        } catch (error) {
            const data = error?.response?.data || {};
            if (Array.isArray(data.node_occupants)) {
                setOccupants(data.node_occupants);
            }
            setErrors(data.fields || {});
            setBanner(data.detail || "The tag could not be saved.");
        } finally {
            setSaving(false);
        }
    };

    const fetchName = async () => {
        if (!values.server || !values.nodeId) {
            setErrors((current) => ({
                ...current,
                server: values.server ? "" : "OPC server is required.",
                nodeId: values.nodeId ? "" : "Node ID is required.",
            }));
            return;
        }
        setFetchingName(true);
        setBanner("");
        try {
            const response = await API.post("/fetch-tag-name/", {
                server_id: values.server,
                node_id: values.nodeId,
            });
            if (response.data?.name) {
                update("name", response.data.name);
                setMessage("Tag name fetched from the OPC node.");
            } else {
                setBanner("Failed to fetch tag name.");
            }
        } catch (error) {
            setBanner(error?.response?.data?.detail || error?.response?.data?.error || "Failed to fetch tag name.");
        } finally {
            setFetchingName(false);
        }
    };

    const copyNodeId = async () => {
        try {
            await navigator.clipboard.writeText(values.nodeId || "");
            setMessage("Node ID copied.");
        } catch (error) {
            setBanner("Failed to copy Node ID.");
        }
    };

    return (
        <form className={`tag-page__create${manual ? "" : " tag-page__create--embedded"}`} onSubmit={submit} noValidate>
            {manual ? null : (
                <p className="tag-page__hint tag-page__field--wide">
                    Selecting a node does not create a tag. Add Tag to System saves this form here.
                </p>
            )}
            {unsupported ? (
                <p className="tag-page__banner is-error tag-page__field--wide" role="alert">{draft.reason}</p>
            ) : null}
            {manual ? null : (
                <p className="tag-page__hint tag-page__field--wide">
                    {sessionCanWrite
                        ? "This OPC account can write the value. Read and write stays off until you choose it. The global OPC write control remains disabled."
                        : "This OPC session does not have CurrentWrite, so Read and write is unavailable."}
                </p>
            )}
            <section className="tag-page__section" aria-labelledby={`${idPrefix}-identity`}>
                <h3 id={`${idPrefix}-identity`}>Identity</h3>
            <Field id={`${idPrefix}-server`} label="OPC Server" required error={fieldError(errors, "server")}>
                <select
                    id={`${idPrefix}-server`}
                    value={values.server}
                    disabled={!manual}
                    aria-invalid={Boolean(errors.server)}
                    onChange={(event) => update("server", event.target.value)}
                >
                    <option value="">Select a server</option>
                    {(servers || []).map((server) => (
                        <option key={server.id} value={server.id}>{server.name}</option>
                    ))}
                </select>
            </Field>
            <Field id={`${idPrefix}-node`} label="Node ID" required error={fieldError(errors, "nodeId") || fieldError(errors, "node_id")}>
                <div className="tag-page__copy-row">
                    <input
                        id={`${idPrefix}-node`}
                        type="text"
                        value={values.nodeId}
                        readOnly={!manual}
                        spellCheck="false"
                        aria-invalid={Boolean(errors.nodeId || errors.node_id)}
                        onChange={(event) => update("nodeId", event.target.value)}
                    />
                    {manual ? null : (
                        <button type="button" className="tag-page__button" onClick={copyNodeId}>Copy</button>
                    )}
                </div>
            </Field>
            <Field id={`${idPrefix}-name`} label="Tag Name" required error={fieldError(errors, "name")}>
                <input
                    id={`${idPrefix}-name`}
                    type="text"
                    value={values.name}
                    aria-invalid={Boolean(errors.name)}
                    onChange={(event) => update("name", event.target.value)}
                />
            </Field>
            </section>
            <section className="tag-page__section" aria-labelledby={`${idPrefix}-signal-heading`}>
                <h3 id={`${idPrefix}-signal-heading`}>Signal / OPC Data Type</h3>
            <Field id={`${idPrefix}-signal`} label="Signal Type" required error={fieldError(errors, "signalType") || fieldError(errors, "signal_type")}>
                <select
                    id={`${idPrefix}-signal`}
                    value={values.signalType}
                    aria-invalid={Boolean(errors.signalType || errors.signal_type)}
                    onChange={(event) => update("signalType", event.target.value)}
                >
                    <option value="">Select signal type</option>
                    <option value="ANALOG">Analog</option>
                    <option value="DIGITAL">Digital</option>
                    <option value="STRING">String</option>
                </select>
            </Field>
            <Field id={`${idPrefix}-datatype`} label="OPC Data Type" required error={fieldError(errors, "opcDataType") || fieldError(errors, "opc_data_type")}>
                <select
                    id={`${idPrefix}-datatype`}
                    value={values.opcDataType}
                    aria-invalid={Boolean(errors.opcDataType || errors.opc_data_type)}
                    onChange={(event) => update("opcDataType", event.target.value)}
                >
                    <option value="">Select OPC data type</option>
                    {OPC_DATA_TYPES.map((typeName) => (
                        <option key={typeName} value={typeName}>{typeName}</option>
                    ))}
                </select>
            </Field>
            {values.signalType === "STRING" ? (
                <p className="tag-page__hint tag-page__field--wide">String values are stored as text. Numeric formulas and charts do not apply.</p>
            ) : null}
            </section>
            <section className="tag-page__section" aria-labelledby={`${idPrefix}-recording-heading`}>
                <h3 id={`${idPrefix}-recording-heading`}>Recording Settings</h3>
            <Field id={`${idPrefix}-recording`} label="Recording Mode" required error={fieldError(errors, "recordingMode") || fieldError(errors, "recording_mode")}>
                <select
                    id={`${idPrefix}-recording`}
                    value={values.recordingMode}
                    aria-invalid={Boolean(errors.recordingMode || errors.recording_mode)}
                    onChange={(event) => update("recordingMode", event.target.value)}
                >
                    {RECORDING_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>{mode.label}</option>
                    ))}
                </select>
            </Field>
            {values.recordingMode === "ON_CHANGE" ? (
                <Field
                    id={`${idPrefix}-interval`}
                    label="On Change Interval"
                    required
                    error={fieldError(errors, "onChangeIntervalSeconds") || fieldError(errors, "on_change_interval_seconds")}
                >
                    <select
                        id={`${idPrefix}-interval`}
                        value={values.onChangeIntervalSeconds}
                        aria-invalid={Boolean(errors.onChangeIntervalSeconds || errors.on_change_interval_seconds)}
                        onChange={(event) => update("onChangeIntervalSeconds", event.target.value)}
                    >
                        {ON_CHANGE_INTERVAL_SECONDS.map((seconds) => (
                            <option key={seconds} value={String(seconds)}>{intervalLabel(seconds)}</option>
                        ))}
                    </select>
                    <div className="tag-page__presets" role="group" aria-label="Common On Change intervals">
                        {ON_CHANGE_INTERVAL_PRESETS.map((seconds) => (
                            <button
                                key={seconds}
                                type="button"
                                className={`tag-page__preset${String(values.onChangeIntervalSeconds) === String(seconds) ? " is-selected" : ""}`}
                                onClick={() => update("onChangeIntervalSeconds", String(seconds))}
                            >
                                {intervalLabel(seconds)}
                            </button>
                        ))}
                    </div>
                </Field>
            ) : null}
            {values.signalType === "STRING" ? (
                <p className="tag-page__hint">Decimal places do not apply to string values.</p>
            ) : (
                <Field id={`${idPrefix}-decimals`} label="Decimal Places" required error={fieldError(errors, "decimalPlaces") || fieldError(errors, "decimal_places")}>
                    <input
                        id={`${idPrefix}-decimals`}
                        type="number"
                        min="0"
                        max="12"
                        step="1"
                        disabled={values.signalType === "DIGITAL"}
                        value={values.signalType === "DIGITAL" ? "0" : values.decimalPlaces}
                        aria-invalid={Boolean(errors.decimalPlaces || errors.decimal_places)}
                        onChange={(event) => update("decimalPlaces", event.target.value)}
                    />
                </Field>
            )}
            <p className="tag-page__hint tag-page__field--wide">
                Recording mode and decimal places apply to future samples. Stored history is left unchanged.
            </p>
            </section>
            <section className="tag-page__section" aria-labelledby={`${idPrefix}-access-heading`}>
                <h3 id={`${idPrefix}-access-heading`}>Access and Lifecycle</h3>
            <Field id={`${idPrefix}-access`} label="Access Mode" required error={fieldError(errors, "accessMode") || fieldError(errors, "access_mode")}>
                <select
                    id={`${idPrefix}-access`}
                    value={values.accessMode}
                    aria-invalid={Boolean(errors.accessMode || errors.access_mode)}
                    onChange={(event) => update("accessMode", event.target.value)}
                >
                    <option value="READ_ONLY">Read only</option>
                    <option value="READ_WRITE" disabled={values.isCalculated || (!manual && !sessionCanWrite)}>Read and write</option>
                </select>
            </Field>
            <Field id={`${idPrefix}-lifecycle`} label="Active / Archived" required error={fieldError(errors, "lifecycle")}>
                <select
                    id={`${idPrefix}-lifecycle`}
                    value={values.lifecycle}
                    aria-invalid={Boolean(errors.lifecycle)}
                    onChange={(event) => update("lifecycle", event.target.value)}
                >
                    <option value="active">Active</option>
                    <option value="archived">Inactive / Archived</option>
                </select>
            </Field>
            </section>
            <section className="tag-page__section" aria-labelledby={`${idPrefix}-group-heading`}>
                <h3 id={`${idPrefix}-group-heading`}>Group and Description</h3>
            <Field id={`${idPrefix}-group`} label="Tag Group" required error={fieldError(errors, "group")}>
                <select
                    id={`${idPrefix}-group`}
                    value={values.group}
                    aria-invalid={Boolean(errors.group)}
                    onChange={(event) => update("group", event.target.value)}
                >
                    <optgroup label="All Tags">
                        {choices.map((choice) => (
                            <option key={choice.value || "none"} value={choice.value}>
                                {`${"\u00A0".repeat(choice.depth * 2)}${choice.label}`}
                            </option>
                        ))}
                    </optgroup>
                </select>
            </Field>
            <Field id={`${idPrefix}-description`} label="Description" wide error={fieldError(errors, "description")}>
                <textarea
                    id={`${idPrefix}-description`}
                    rows="3"
                    value={values.description}
                    aria-invalid={Boolean(errors.description)}
                    onChange={(event) => update("description", event.target.value)}
                />
            </Field>
            </section>
            <section className="tag-page__section" aria-labelledby={`${idPrefix}-formula-heading`}>
                <h3 id={`${idPrefix}-formula-heading`}>Formula</h3>
            {values.signalType === "STRING" ? (
                <p className="tag-page__hint tag-page__field--wide">Numeric formulas are not available for string tags.</p>
            ) : (
                <div className="tag-page__field tag-page__field--wide">
                    <label className="tag-page__check" htmlFor={`${idPrefix}-calculated`}>
                        <input
                            id={`${idPrefix}-calculated`}
                            type="checkbox"
                            checked={Boolean(values.isCalculated)}
                            onChange={(event) => update("isCalculated", event.target.checked)}
                        />
                        Calculated tag
                    </label>
                    <p className="tag-page__hint">
                        {values.isCalculated
                            ? "Calculated tags are read only. They read this OPC node and store the formula result. They never write to the PLC."
                            : "Off by default. A physical tag stores the OPC value. A formula is not required."}
                    </p>
                </div>
            )}
            {values.isCalculated && values.signalType !== "STRING" ? (
                <Field id={`${idPrefix}-formula`} label="Formula" required wide error={fieldError(errors, "formula")}>
                    <input
                        id={`${idPrefix}-formula`}
                        type="text"
                        value={values.formula || ""}
                        placeholder="x * 0.1"
                        spellCheck="false"
                        aria-invalid={Boolean(errors.formula)}
                        onChange={(event) => update("formula", event.target.value)}
                    />
                </Field>
            ) : null}
            </section>
            <p className="tag-page__hint tag-page__field--wide">
                OPC data type is stored in Telemindra Industrial. It does not change the PLC node. A read/write tag still cannot write while the global OPC write control is off.
            </p>
            {share.level === "blocked" ? (
                <p className="tag-page__banner is-error tag-page__field--wide" role="alert">{share.message}</p>
            ) : null}
            {share.level === "warning" ? (
                <p className="tag-page__banner is-pending tag-page__field--wide" role="status">{share.message}</p>
            ) : null}
            {share.physical.length > 0 || share.calculated.length > 0 ? (
                <ul className="tag-page__occupants tag-page__field--wide">
                    {share.physical.map((row) => (
                        <li key={`physical-${row.id}`}>Physical · {row.is_active ? "Active" : "Archived"} · {row.name}</li>
                    ))}
                    {share.calculated.map((row) => (
                        <li key={`calculated-${row.id}`}>Calculated · {row.is_active ? "Active" : "Archived"} · {row.name}</li>
                    ))}
                </ul>
            ) : null}
            {alreadyRegistered && occupants.length === 0 ? (
                <p className="tag-page__banner is-pending tag-page__field--wide" role="status">
                    This node is already registered on this server. A calculated tag can share it when the server accepts the formula.
                </p>
            ) : null}
            {banner ? <p className="tag-page__banner is-error tag-page__field--wide" role="alert">{banner}</p> : null}
            {message ? <p className="tag-page__banner is-success tag-page__field--wide" role="status">{message}</p> : null}
            <div className="tag-page__actions tag-page__field--wide">
                {manual ? (
                    <button type="button" className="tag-page__button" onClick={fetchName} disabled={fetchingName || saving}>
                        {fetchingName ? "Fetching..." : "Fetch Tag Name"}
                    </button>
                ) : null}
                <button
                    type="submit"
                    className="tag-page__button tag-page__button--primary"
                    disabled={saving || unsupported || share.level === "blocked"}
                >
                    {saving ? "Saving…" : submitLabel}
                </button>
            </div>
        </form>
    );
}

function Field({ id, label, required, error, wide, children }) {
    const errorId = `${id}-error`;
    return (
        <div className={`tag-page__field${wide ? " tag-page__field--wide" : ""}`}>
            <label htmlFor={id}>
                {label}
                {required ? <span className="tag-page__required"> Required</span> : null}
            </label>
            {children}
            {error ? <p id={errorId} className="tag-page__field-error" role="alert">{error}</p> : null}
        </div>
    );
}
